import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { BookOpen, Sparkles, Users } from 'lucide-react';
import SEO from '../components/SEO';
import { useAuth } from '../auth/AuthProvider';
import { useTheme } from '../theme';
import { useSharedCourse } from '../features/courseShare/hooks/useSharedCourse';
import { setPendingShareAccept, getPendingShareAccept } from '../features/courseShare/pendingShare';
import ErrorBlock from '../features/courseShare/components/ErrorBlock';
import { Skeleton, SkeletonText, SkeletonRegion } from '../components/ui/Skeleton';
import Button from '../features/courseShare/components/Button';
import graspdLogo from '../assets/graspd-logo.svg';

const DIFFICULTY_LABELS = {
  BEGINNER: 'Beginner',
  INTERMEDIATE: 'Intermediate',
  ADVANCED: 'Advanced',
};

// Public recipient-facing preview for a shared course, rendered as a focused
// standalone window (no app sidebar/topbar — see ConditionalLayout in App.js).
//
// Flow: the recipient reads the course, then hits ONE button — "Add to my
// courses". If they still need to sign in and/or subscribe, we queue the intent
// (localStorage) and route them through login -> subscription. The intent
// survives both redirects and auto-fires the moment they're eligible, so the
// course is created the instant they subscribe — no second click. The course
// they get is regenerated from the shared spec on their own account, not copied.
export default function CourseSharePreviewPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const { user, ready, isSubscribed, subscriptionLoading } = useAuth();
  const { data, loading, error, reload } = useSharedCourse(token);
  const [submitting, setSubmitting] = useState(false);
  const nudgedRef = useRef(false);


  // One button drives the whole flow. Queue the intent, then move the user
  // toward eligibility. The actual course build is owned by PendingShareResumer
  // (app root), which fires the instant the user is authenticated + subscribed —
  // whether that's right now (already subscribed) or after login + checkout.
  const handleCreate = () => {
    if (submitting) return;
    setPendingShareAccept(token, { uid: user?.uid || null });
    if (!user) {
      // Login reads its return path from router state (location.state.from).
      navigate('/login', { state: { from: { pathname: `/shared/${token}` } } });
      return;
    }
    if (!subscriptionLoading && !isSubscribed) {
      // Send them to the subscription menu; the resumer builds the course the
      // instant checkout completes and the subscription goes active.
      navigate('/dashboard');
      return;
    }
    // Already subscribed: setPendingShareAccept emits an event the resumer
    // reacts to; it takes over with its full-screen builder. Disable the button
    // until that overlay appears.
    setSubmitting(true);
  };

  // After the sign-in hop the user returns here. If they're authenticated but
  // not yet subscribed, continue the chain automatically to the subscription
  // menu — no second click. (The subscribed case is owned by the resumer.)
  useEffect(() => {
    if (loading || !data || nudgedRef.current) return;
    // Wait for auth: reading the queued token with an unresolved uid fails the
    // owner check and would skip the nudge.
    if (!ready) return;
    if (getPendingShareAccept({ uid: user?.uid || null }) !== token) return;
    if (!user || subscriptionLoading || isSubscribed) return;
    nudgedRef.current = true;
    navigate('/dashboard');
  }, [loading, data, ready, user, isSubscribed, subscriptionLoading, token, navigate]);

  return (
    <div style={windowStyle}>
      <SEO title={data ? `${data.title} — shared on Graspd` : 'Shared course'} path={`/shared/${token}`} noindex />

      {/* Minimal brand — focused window, no nav */}
      <header style={brandBar}>
        <Link to="/" aria-label="Graspd home" style={{ display: 'inline-flex' }}>
          <img src={graspdLogo} alt="Graspd" style={{ height: 22, width: 'auto', display: 'block', filter: resolvedTheme === 'dark' ? 'invert(1)' : 'none' }} />
        </Link>
      </header>

      <main style={contentColumn}>
        {loading ? (
          /* The only unauthenticated page a prospective user lands on from a
             shared link, so it gets a real shape rather than a spinner. */
          <SkeletonRegion label="Loading shared course">
            <Skeleton width="72%" height={30} style={{ marginBottom: 'var(--space-3)' }} />
            <Skeleton width="38%" height={14} style={{ marginBottom: 'var(--space-5)' }} />
            <SkeletonText lines={3} style={{ marginBottom: 'var(--space-6)' }} />
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} height={14} width={`${92 - i * 6}%`} style={{ marginBottom: 'var(--space-3)' }} />
            ))}
            <Skeleton height={44} radius="var(--radius-control)" style={{ marginTop: 'var(--space-6)' }} />
          </SkeletonRegion>
        ) : error || !data ? (
          <>
            <ErrorBlock message={error || 'This shared course could not be found.'} onRetry={reload} />
            <div style={{ textAlign: 'center', marginTop: 18 }}>
              <Link to="/" style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-body-size)', fontWeight: 600 }}>
                Go to Graspd home
              </Link>
            </div>
          </>
        ) : (
          <SharedCourseBody
            data={data}
            user={user}
            isSubscribed={isSubscribed}
            subscriptionLoading={subscriptionLoading}
            submitting={submitting}
            onCreate={handleCreate}
          />
        )}
      </main>
    </div>
  );
}

function SharedCourseBody({ data, user, isSubscribed, subscriptionLoading, submitting, onCreate }) {
  const { title, summary, difficulty, outline = [], creatorDisplayName, acceptCount = 0 } = data;
  const totalLessons = outline.reduce((sum, m) => sum + (m.lessonCount || 0), 0);
  const difficultyLabel = DIFFICULTY_LABELS[difficulty] || difficulty;

  return (
    <div style={cardStyle}>
      {/* Eyebrow */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, color: 'var(--text-secondary)' }}>
        <Sparkles size={16} style={{ color: 'var(--accent-primary)' }} />
        <span style={{ fontSize: 'var(--text-body-size)', fontWeight: 600 }}>
          {creatorDisplayName ? `${creatorDisplayName} shared a course with you` : 'A course was shared with you'}
        </span>
      </div>

      {/* Title */}
      <h1 style={{ margin: '0 0 14px', fontSize: 'var(--text-page-size)', lineHeight: 1.15, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-primary)' }}>
        {title}
      </h1>

      {/* Meta row */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 22 }}>
        {difficultyLabel && <span style={pillStyle}>{difficultyLabel}</span>}
        {outline.length > 0 && (
          <span style={metaChip}>
            <BookOpen size={14} style={{ color: 'var(--text-tertiary)' }} />
            {outline.length} {outline.length === 1 ? 'module' : 'modules'}
            {totalLessons > 0 ? ` · ${totalLessons} lessons` : ''}
          </span>
        )}
        {acceptCount > 0 && (
          <span style={metaChip}>
            <Users size={14} style={{ color: 'var(--text-tertiary)' }} />
            {acceptCount} {acceptCount === 1 ? 'person has' : 'people have'} taken this
          </span>
        )}
      </div>

      {/* Summary */}
      {summary && <p style={{ margin: '0 0 28px', fontSize: 'var(--text-panel-size)', lineHeight: 1.7, color: 'var(--text-secondary)' }}>{summary}</p>}

      {/* Outline */}
      {outline.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <h2 style={sectionLabel}>What&apos;s inside</h2>
          <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {outline.map((mod, i) => (
              <li key={i} style={moduleRow}>
                <span style={moduleNum}>{i + 1}</span>
                <span style={{ flex: 1, fontSize: 'var(--text-panel-size)', fontWeight: 600, color: 'var(--text-primary)' }}>{mod.title}</span>
                {mod.lessonCount > 0 && (
                  <span style={{ fontSize: 'var(--text-body-size)', color: 'var(--text-tertiary)', flexShrink: 0 }}>
                    {mod.lessonCount} {mod.lessonCount === 1 ? 'lesson' : 'lessons'}
                  </span>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* CTA */}
      <div style={ctaCard}>
        <AcceptCta
          user={user}
          isSubscribed={isSubscribed}
          subscriptionLoading={subscriptionLoading}
          submitting={submitting}
          onCreate={onCreate}
        />
      </div>
    </div>
  );
}

function AcceptCta({ user, isSubscribed, subscriptionLoading, submitting, onCreate }) {
  // Everyone gets the SAME button. The click handler queues the intent and
  // routes through sign-in / subscription as needed; PendingShareResumer builds
  // the course (with its own full-screen overlay) once the user is eligible.
  const needsSetup = !user || (!subscriptionLoading && !isSubscribed);

  return (
    <div>
      <Button onClick={onCreate} loading={submitting} style={{ fontSize: 'var(--text-panel-size)' }}>
        {submitting ? 'Adding your course…' : 'Add to my courses'}
      </Button>
      <p style={{ margin: '10px 0 0', fontSize: 'var(--text-body-size)', color: 'var(--text-tertiary)', lineHeight: 1.6 }}>
        {needsSetup
          ? 'You’ll pick a plan to finish — your course generates the moment you subscribe, and your progress stays private to you.'
          : 'Graspd builds a fresh course from this outline on your account. Your progress stays private to you.'}
      </p>
    </div>
  );
}

const windowStyle = {
  minHeight: '100vh',
  background: 'var(--bg-primary)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
};

const brandBar = {
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  padding: '22px 20px 6px',
};

const contentColumn = {
  width: '100%',
  maxWidth: 'var(--frame-prose)',
  padding: '24px 20px 64px',
};

const cardStyle = {
  width: '100%',
};

const ctaCard = {
  padding: '20px 22px',
  borderRadius: 'var(--radius-panel)',
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border-subtle)',
};

const pillStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 24,
  padding: '0 10px',
  borderRadius: 'var(--radius-control)',
  fontSize: 'var(--text-numeric-size)',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  color: 'var(--text-inverted)',
  background: 'var(--accent-primary)',
};

const metaChip = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 'var(--text-body-size)',
  color: 'var(--text-secondary)',
};

const sectionLabel = {
  margin: '0 0 12px',
  fontSize: 'var(--text-body-size)',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--text-tertiary)',
};

const moduleRow = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 14px',
  borderRadius: 'var(--radius-panel)',
  background: 'var(--glass-bg-light)',
  border: '1px solid var(--border-subtle)',
};

const moduleNum = {
  flexShrink: 0,
  width: 26,
  height: 26,
  display: 'grid',
  placeItems: 'center',
  borderRadius: 'var(--radius-control)',
  background: 'var(--glass-bg-medium)',
  fontSize: 'var(--text-body-size)',
  fontWeight: 700,
  color: 'var(--text-secondary)',
};
